import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Download, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface CSVExportButtonProps {
  area?: string;
  type?: string;
  year?: number;
  className?: string;
}

// Traffic-first model: CSV export is free for everyone, no account or quota.
const CSVExportButton = ({ 
  area = "United States", 
  type = "all", 
  year = 2025,
  className = ""
}: CSVExportButtonProps) => {
  const [isExporting, setIsExporting] = useState(false);

  const handleExport = async () => {
    setIsExporting(true);
    
    try {
      const { data, error } = await supabase.functions.invoke('csv-export', {
        body: { area, type, year }
      });

      if (error) {
        throw error;
      }

      // Create download
      const blob = new Blob([data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${area}_${type}_wages_${year}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success("CSV exported successfully!");
    } catch (error) {
      console.error('Export error:', error);
      toast.error("Failed to export data. Please try again.");
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Button
      onClick={handleExport}
      disabled={isExporting}
      variant="outline"
      size="sm"
      aria-label="Export CSV"
      className={`shrink-0 ${className}`}
    >
      {isExporting ? (
        <Loader2 className="w-4 h-4 mr-1.5 sm:mr-2 animate-spin" />
      ) : (
        <Download className="w-4 h-4 mr-1.5 sm:mr-2" />
      )}
      {isExporting ? (
        <>
          <span className="sm:hidden">...</span>
          <span className="hidden sm:inline">Exporting...</span>
        </>
      ) : (
        <>
          <span className="sm:hidden">CSV</span>
          <span className="hidden sm:inline">Export CSV</span>
        </>
      )}
    </Button>
  );
};

export default CSVExportButton;
